// ── Juntar las conversaciones de una misma persona ───────────────────────────
// La misma persona entra dos veces: escribe por WhatsApp y se le crea un
// contacto con su número, después llena un formulario con su correo y otro
// teléfono, y queda una segunda ficha. Ninguna de las dos llaves coincide, así
// que nada las vincula solo — y su conversación queda partida en dos ventanas.
//
// Caso real (Villa Sión, 2026-09-15): "Frauny Peña" por WhatsApp con
// +18093560850, y "Frauny Peña" por formulario con fraunypena2@gmail.com y un
// teléfono mal tipeado.
//
// Se ELIGE campo por campo, no se acepta un resultado ya decidido. La primera
// versión mostraba las fichas y fusionaba con una regla fija —"la que queda
// gana, los huecos los llena la otra"— y eso no sirve cuando lo que querés es
// justamente el dato de la otra: el teléfono mal tipeado del formulario gana
// sobre el bueno de WhatsApp solo porque el que queda tenía el campo vacío, o
// al revés. Quien mira las dos fichas sabe cuál valor es el bueno; el programa
// no.
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries } from '@tanstack/react-query';
import { Modal } from '../ui/Modal.js';
import { Button } from '../ui/Button.js';
import { Pressable } from '../ui/Pressable.js';
import { LoadingState } from '../ui/LoadingState.js';
import { Icon } from '../Icon.js';
import { api, ApiError } from '../../lib/api.js';
import { useToast } from '../ui/Toast.js';
import { useMergeContacts, useUpdateContact, type Contact } from '../../hooks/useContacts.js';
import { useLinkToContact } from '../../hooks/useDmTriage.js';

export interface ConversacionElegida {
  /** null cuando el remitente todavía no está vinculado a nadie. */
  contactId: number | null;
  nombre: string;
  /** Todos los canales de esa conversación: se vinculan juntos. */
  channelIds: number[];
}

export interface FusionarContactosProps {
  open: boolean;
  onClose: () => void;
  conversaciones: ConversacionElegida[];
  onHecho: () => void;
}

/** Los campos que se eligen. El resto —notas, teléfonos y correos adicionales,
 *  y todo lo que cuelga— se suma siempre: ahí no hay nada que descartar. */
const CAMPOS = ['firstName', 'lastName', 'businessName', 'primaryEmail', 'primaryPhone', 'category'] as const;
type Campo = (typeof CAMPOS)[number];

export function FusionarContactos({ open, onClose, conversaciones, onHecho }: FusionarContactosProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const fusionar = useMergeContacts();
  const actualizar = useUpdateContact();
  const vincular = useLinkToContact();
  const [trabajando, setTrabajando] = useState(false);

  /** Qué ficha aporta cada campo. Se arranca con la más antigua y se cambia
   *  tocando el valor de la otra. */
  const [elegido, setElegido] = useState<Record<Campo, number | null>>({
    firstName: null, lastName: null, businessName: null,
    primaryEmail: null, primaryPhone: null, category: null,
  });

  const sinContacto = conversaciones.filter(c => c.contactId == null);
  const ids = [...new Set(conversaciones.filter(c => c.contactId != null).map(c => c.contactId!))];

  const fichas = useQueries({
    queries: ids.map(id => ({
      queryKey: ['contact', id],
      queryFn: () => api.get<Contact>(`/api/contacts/${id}`),
      enabled: open,
    })),
  });
  const cargando = fichas.some(q => q.isLoading);
  const datos = useMemo(
    () => fichas.map(q => q.data).filter((c): c is Contact => c != null),
    [fichas],
  );

  /** La más antigua es la que sobrevive: arrastra la historia de esa persona,
   *  y es de la que cuelgan sus conversaciones más viejas. */
  const queQueda = useMemo(() => {
    if (datos.length === 0) return null;
    return [...datos].sort((a, b) => (a.firstSeen ?? '').localeCompare(b.firstSeen ?? ''))[0]!;
  }, [datos]);

  const otras = datos.filter(c => c.id !== queQueda?.id);

  // Arranca con lo que tiene la que queda; donde ella no tiene nada, propone el
  // valor de la otra. Es lo que uno querría el 90% de las veces, y lo otro se
  // cambia con un clic.
  useEffect(() => {
    if (!queQueda) return;
    const inicial = {} as Record<Campo, number | null>;
    for (const campo of CAMPOS) {
      const suyo = valorDe(queQueda, campo);
      const otra = otras.find(o => valorDe(o, campo));
      inicial[campo] = suyo ? queQueda.id : (otra?.id ?? queQueda.id);
    }
    setElegido(inicial);
    // Solo al abrir con fichas nuevas: no pisa lo que la persona ya eligió.
  }, [queQueda?.id, datos.length, open]);

  const puedeFusionar = queQueda != null && otras.length > 0;
  const soloVincular = queQueda != null && otras.length === 0 && sinContacto.length > 0;

  async function aplicar() {
    if (!queQueda) return;
    setTrabajando(true);
    try {
      // 1. Juntar. `mergeContacts` muda todo lo que cuelga y suma los datos
      //    adicionales de las dos (teléfonos, correos, notas, etiquetas).
      for (const otra of otras) {
        await fusionar.mutateAsync({ keepId: queQueda.id, mergeId: otra.id });
      }

      // 2. Escribir lo elegido. Va DESPUÉS de fusionar: antes, el índice único
      //    de correo no dejaría poner en una ficha el correo que todavía tiene
      //    la otra.
      const patch: Record<string, string | null> = {};
      for (const campo of CAMPOS) {
        const fuente = datos.find(c => c.id === elegido[campo]);
        const valor = fuente ? valorDe(fuente, campo) : null;
        if (valor && valor !== valorDe(queQueda, campo)) patch[campo] = valor;
      }
      if (Object.keys(patch).length > 0) {
        await actualizar.mutateAsync({ id: queQueda.id, ...patch });
      }

      // 3. Los remitentes que no eran de nadie pasan a colgar de esta ficha.
      const canalesSueltos = sinContacto.flatMap(c => c.channelIds);
      if (canalesSueltos.length > 0) {
        await vincular.mutateAsync({ channelIds: canalesSueltos, contactId: queQueda.id });
      }

      toast.success(t('messages.mergeDone', { name: nombreDe(queQueda) }));
      onHecho();
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('messages.mergeTitle')}
      description={t('messages.mergeHint')}
      size="lg"
    >
      {cargando ? (
        <LoadingState size="sm" />
      ) : !queQueda ? (
        <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)' }}>
          {t('messages.mergeNeedsContact')}
        </span>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
          {puedeFusionar && (
            <>
              <div style={{ display: 'flex', gap: 'var(--evo-space-3)', alignItems: 'center' }}>
                <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)' }}>
                  {t('messages.mergePickHint')}
                </span>
              </div>

              {CAMPOS.map(campo => (
                <FilaDeCampo
                  key={campo}
                  campo={campo}
                  fichas={[queQueda, ...otras]}
                  elegidoId={elegido[campo]}
                  onElegir={id => setElegido(prev => ({ ...prev, [campo]: id }))}
                />
              ))}

              <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)' }}>
                {t('messages.mergeKeepsRest')}
              </span>
            </>
          )}

          {sinContacto.length > 0 && (
            <div style={{
              display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)',
              padding: 'var(--evo-space-3)', borderRadius: 'var(--evo-r)', background: 'var(--evo-bg-2)',
            }}>
              <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-2)' }}>
                {t('messages.mergeWillLink', { count: sinContacto.length, name: nombreDe(queQueda) })}
              </span>
              <span style={{ fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-fg-4)' }}>
                {sinContacto.map(c => c.nombre).join(' · ')}
              </span>
            </div>
          )}

          {puedeFusionar && (
            <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-warning)' }}>
              {t('messages.mergeIrreversible')}
            </span>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--evo-space-3)' }}>
            <Button variant="ghost" tone="muted" size="sm" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="solid" tone="cyan" size="sm"
              loading={trabajando}
              disabled={!puedeFusionar && !soloVincular}
              onClick={() => { void aplicar(); }}
            >
              {puedeFusionar ? t('messages.mergeAction') : t('messages.mergeLinkAction')}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/** Un campo, con el valor de cada ficha al lado. Se toca el que se quiere. */
function FilaDeCampo({ campo, fichas, elegidoId, onElegir }: {
  campo: Campo;
  fichas: Contact[];
  elegidoId: number | null;
  onElegir: (id: number) => void;
}) {
  const { t } = useTranslation();
  const opciones = fichas
    .map(f => ({ id: f.id, valor: valorDe(f, campo) }))
    .filter(o => o.valor);

  // Un campo que ninguna de las dos tiene no se muestra: no hay nada que elegir.
  if (opciones.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
      <span style={{
        fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-fg-4)',
        textTransform: 'uppercase', letterSpacing: '0.04em',
      }}>
        {t(`messages.mergeField.${campo}`)}
      </span>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--evo-space-2)' }}>
        {opciones.map(o => {
          const activo = o.id === elegidoId;
          return (
            <Pressable key={o.id} onPress={() => onElegir(o.id)} ariaLabel={o.valor!}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 'var(--evo-space-2)',
                padding: 'var(--evo-space-2) var(--evo-space-3)',
                borderRadius: 'var(--evo-r)',
                border: `1px solid ${activo ? 'var(--evo-cyan)' : 'var(--evo-stroke)'}`,
                background: activo ? 'var(--evo-chat-bubble-out-bg)' : 'var(--evo-bg-2)',
                fontSize: 'var(--evo-fs-caption)',
                color: activo ? 'var(--evo-fg-1)' : 'var(--evo-fg-3)',
                textAlign: 'left',
              }}>
                {activo && <Icon name="check" size={12} />}
                {campo === 'category'
                  ? t(`contacts.category.${o.valor!}`, { defaultValue: o.valor! })
                  : o.valor}
              </span>
            </Pressable>
          );
        })}
      </div>
    </div>
  );
}

function valorDe(c: Contact, campo: Campo): string | null {
  const v = c[campo];
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

function nombreDe(c: Contact): string {
  return [c.firstName, c.lastName].filter(Boolean).join(' ').trim()
    || c.businessName?.trim()
    || c.primaryEmail
    || `#${c.id}`;
}
